import React, { useState, useEffect, useCallback, useMemo, useRef, forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import './BlogAdminPage.css';

// Create a custom wrapper component for ReactQuill
const QuillWrapper = forwardRef((props, ref) => {
  const quillRef = useRef(null);
  
  // Forward the ref to the ReactQuill instance
  React.useImperativeHandle(ref, () => ({
    getEditor: () => quillRef.current?.getEditor(),
    getQuill: () => quillRef.current,
  }));

  return (
    <div className="quill-wrapper">
      <ReactQuill
        ref={quillRef}
        {...props}
        bounds={false}
      />
    </div>
  );
});

QuillWrapper.displayName = 'QuillWrapper';

const BlogAdminPage = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    slug: '',
    featured_image: '',
    published: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const quillRef = useRef(null);

  // Initialize editor after mount
  useEffect(() => {
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      if (editor) {
        // Set up any initial editor configurations here
        editor.focus();
      }
    }
  }, []);

  // Image upload handler
  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];

      if (!file || !file.type.startsWith('image/')) {
        setError('Only image files are allowed');
        return;
      }

      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;

      try {
        const { data, error } = await supabase.storage
          .from('blog-images')
          .upload(fileName, file, {
            cacheControl: '3600',
            contentType: file.type,
            upsert: false
          });

        if (error) {
          setError(`Upload failed: ${error.message}`);
          return;
        }

        const { data: { publicUrl } } = supabase.storage
          .from('blog-images')
          .getPublicUrl(data.path);

        // Insert into editor using the ref
        const editor = quillRef.current?.getEditor();
        if (editor) {
          const range = editor.getSelection(true);
          editor.insertEmbed(range.index, 'image', publicUrl);
        }

      } catch (error) {
        setError('Failed to upload image');
      }
    };
  }, [setError]);

  // Quill editor modules configuration
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        ['link', 'image'],
        [{ 'align': [] }],
        [{ 'color': [] }],
        ['clean']
      ],
      handlers: {
        image: imageHandler
      }
    },
    clipboard: {
      matchVisual: false
    }
  }), [imageHandler]);

  const formats = useMemo(() => [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'link', 'image',
    'align',
    'color'
  ], []);

  const handleContentChange = useCallback((content) => {
    setFormData(prev => ({ ...prev, content: content || '' }));
  }, []);

  // Function to check database schema and permissions
  const checkDatabaseSchema = useCallback(async () => {
    try {
      console.log('Starting database schema and permissions check...');
      
      // Log the current session
      const { data: { session } } = await supabase.auth.getSession();
      console.log('Current session details:', {
        isAuthenticated: !!session,
        user: session?.user?.email,
        role: session?.user?.role,
        id: session?.user?.id
      });

      if (!session) {
        console.error('No active session found - authentication required');
        return false;
      }
      
      // Check if we can read from the blog_posts table
      console.log('Testing READ permission...');
      const { data: readData, error: readError } = await supabase
        .from('blog_posts')
        .select('id, title, published')
        .limit(1);
      
      if (readError) {
        console.error('READ permission test failed:', {
          code: readError.code,
          message: readError.message,
          details: readError.details,
          hint: readError.hint
        });
        return false;
      }
      
      console.log('READ permission test successful:', {
        hasData: !!readData,
        recordCount: readData?.length || 0
      });
      
      // Check if we can insert into the blog_posts table
      console.log('Testing INSERT permission...');
      const testPost = {
        title: 'Permission Test Post',
        content: 'Testing database permissions',
        slug: 'test-permissions-' + Date.now(),
        published: false,
        author: session.user.email,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      console.log('Attempting to insert test post:', testPost);
      const { data: insertData, error: insertError } = await supabase
        .from('blog_posts')
        .insert([testPost])
        .select();
      
      if (insertError) {
        console.error('INSERT permission test failed:', {
          code: insertError.code,
          message: insertError.message,
          details: insertError.details,
          hint: insertError.hint
        });
        return false;
      }
      
      const insertedId = insertData?.[0]?.id;
      console.log('INSERT permission test successful:', {
        insertedId,
        insertedData: insertData?.[0]
      });
      
      // Test UPDATE permission if insert was successful
      if (insertedId) {
        console.log('Testing UPDATE permission...');
        const { data: updateData, error: updateError } = await supabase
          .from('blog_posts')
          .update({ title: 'Updated Test Post' })
          .eq('id', insertedId)
          .select();
          
        if (updateError) {
          console.error('UPDATE permission test failed:', {
            code: updateError.code,
            message: updateError.message,
            details: updateError.details,
            hint: updateError.hint
          });
        } else {
          console.log('UPDATE permission test successful:', {
            updatedData: updateData?.[0]
          });
        }
        
        // Test DELETE permission
        console.log('Testing DELETE permission...');
        const { error: deleteError } = await supabase
          .from('blog_posts')
          .delete()
          .eq('id', insertedId);
        
        if (deleteError) {
          console.error('DELETE permission test failed:', {
            code: deleteError.code,
            message: deleteError.message,
            details: deleteError.details,
            hint: deleteError.hint
          });
        } else {
          console.log('DELETE permission test successful');
        }
      }
      
      return true;
    } catch (err) {
      console.error('Database permission check failed:', {
        error: err,
        message: err.message,
        stack: err.stack
      });
      return false;
    }
  }, []);

  // Enhanced session check with token validation
  const checkSession = useCallback(async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('Session check error:', error);
        navigate('/login');
        return false;
      }

      if (!session) {
        console.log('No active session in BlogAdminPage');
        navigate('/login');
        return false;
      }

      // Check if session is expired
      const now = new Date().getTime() / 1000;
      if (session.expires_at < now) {
        console.log('Session expired');
        navigate('/login');
        return false;
      }

      // Verify the session is still valid
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        console.error('User verification failed:', userError);
        navigate('/login');
        return false;
      }

      console.log('Active session found for user:', user.email);
      return true;
    } catch (error) {
      console.error('Session check error:', error);
      navigate('/login');
      return false;
    }
  }, [navigate]);

  // Add auth check to all database operations
  const fetchPosts = useCallback(async () => {
    try {
      const hasValidSession = await checkSession();
      if (!hasValidSession) return;

      const { data, error } = await supabase
        .from('blog_posts')
        .select('id, title, content, excerpt, slug, featured_image, published, created_at')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPosts(data || []);
    } catch (err) {
      setError('Failed to fetch posts');
    } finally {
      setIsLoading(false);
    }
  }, [checkSession]);

  // Function to test updating the published field
  const testPublishField = useCallback(async () => {
    try {
      console.log('Testing published field update...');
      
      // Create a test post
      const testPost = {
        title: 'Publish Test Post',
        content: 'This is a test post for publishing',
        slug: 'publish-test-' + Date.now(),
        published: false,
        author: 'The Learning Dictionary',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      const { data: insertData, error: insertError } = await supabase
        .from('blog_posts')
        .insert([testPost])
        .select();
      
      if (insertError) {
        console.error('Error creating test post:', insertError);
        return false;
      }
      
      if (!insertData || insertData.length === 0) {
        console.error('No data returned after inserting test post');
        return false;
      }
      
      const testPostId = insertData[0].id;
      console.log('Created test post with ID:', testPostId);
      
      // Update the published field
      const { data: updateData, error: updateError } = await supabase
        .from('blog_posts')
        .update({ published: true })
        .eq('id', testPostId)
        .select();
      
      if (updateError) {
        console.error('Error updating published field:', updateError);
        return false;
      }
      
      if (!updateData || updateData.length === 0) {
        console.error('No data returned after updating published field');
        return false;
      }
      
      console.log('Successfully updated published field:', updateData[0].published);
      
      // Delete the test post
      const { error: deleteError } = await supabase
        .from('blog_posts')
        .delete()
        .eq('id', testPostId);
      
      if (deleteError) {
        console.error('Error deleting test post:', deleteError);
      } else {
        console.log('Successfully deleted test post');
      }
      
      return true;
    } catch (err) {
      console.error('Error testing published field:', err);
      return false;
    }
  }, []);

  // Add auth check to handleSubmit
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    if (isSubmitting) {
      console.log('Already submitting, please wait...');
      return;
    }

    try {
      const hasValidSession = await checkSession();
      if (!hasValidSession) return;

      setIsSubmitting(true);
      setError(null);

      // Validate form data
      if (!formData.title || !formData.content || !formData.slug) {
        setError('Title, content, and slug are required');
        return;
      }

      console.log('Saving post...', {
        title: formData.title,
        contentLength: formData.content?.length,
        hasContent: !!formData.content
      });

      const updates = {
        title: formData.title.trim(),
        content: formData.content,
        excerpt: formData.excerpt || '',
        slug: formData.slug.toLowerCase(),
        featured_image: formData.featured_image || null,
        published: formData.published,
        author: 'The Learning Dictionary'
      };

      let error;

      if (editingPost) {
        console.log('Updating existing post:', editingPost.id);
        const { error: updateError } = await supabase
          .from('blog_posts')
          .update(updates)
          .eq('id', editingPost.id);
        error = updateError;
      } else {
        console.log('Creating new post');
        const { error: insertError } = await supabase
          .from('blog_posts')
          .insert([updates]);
        error = insertError;
      }

      if (error) {
        console.error('Error saving post:', error);
        throw error;
      }

      console.log('Post saved successfully');

      // Reset form
      setFormData({
        title: '',
        content: '',
        excerpt: '',
        slug: '',
        featured_image: '',
        published: false
      });
      
      setEditingPost(null);
      await fetchPosts();
      
    } catch (err) {
      console.error('Error saving post:', err);
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (post) => {
    console.log('Editing post:', post);
    setEditingPost(post);
    setFormData({
      title: post.title,
      content: post.content,
      excerpt: post.excerpt || '',
      slug: post.slug,
      featured_image: post.featured_image || '',
      published: post.published
    });
  };

  // Add auth check to handleDelete
  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) {
      return;
    }

    try {
      const hasValidSession = await checkSession();
      if (!hasValidSession) return;

      console.log('Deleting post:', postId);
      const { error } = await supabase
        .from('blog_posts')
        .delete()
        .eq('id', postId);

      if (error) throw error;

      console.log('Post deleted successfully');
      await fetchPosts();
    } catch (err) {
      console.error('Error deleting post:', err);
      setError(err.message);
    }
  };

  const handleCancel = () => {
    setEditingPost(null);
    setFormData({
      title: '',
      content: '',
      excerpt: '',
      slug: '',
      featured_image: '',
      published: false
    });
  };

  useEffect(() => {
    const initializePage = async () => {
      const hasSession = await checkSession();
      if (hasSession) {
        // Check database schema and permissions
        const schemaCheck = await checkDatabaseSchema();
        if (!schemaCheck) {
          setError('Database schema check failed. Please check console for details.');
        }
        
        // Test the published field
        const publishCheck = await testPublishField();
        if (!publishCheck) {
          setError('Published field test failed. Please check console for details.');
        }
        
        await fetchPosts();
      }
    };

    initializePage();

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state changed:', event, session?.user?.email);
      if (!session) {
        navigate('/login');
      } else if (event === 'SIGNED_IN') {
        await fetchPosts();
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [checkSession, fetchPosts, navigate, checkDatabaseSchema, testPublishField]);

  // Call the test function when the component mounts
  useEffect(() => {
    testDatabaseConnection();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => {
      const updates = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };
      
      if (name === 'title') {
        const generatedSlug = value
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '')
          .substring(0, 100);
        
        updates.slug = generatedSlug;
      }
      
      return updates;
    });
  };

  // Add auth check to togglePublishedStatus
  const togglePublishedStatus = async (postId, currentStatus) => {
    try {
      const hasValidSession = await checkSession();
      if (!hasValidSession) return;

      const { data, error } = await supabase
        .from('blog_posts')
        .update({ published: !currentStatus })
        .eq('id', postId)
        .select()
        .single();
      
      if (error) throw error;
      
      fetchPosts();
    } catch (err) {
      console.error('Error toggling published status:', err);
      setError(err.message);
    }
  };

  // Add a function to test the database connection
  const testDatabaseConnection = async () => {
    try {
      console.log('Testing database connection...');
      const { data, error } = await supabase
        .from('blog_posts')
        .select('id')
        .limit(1);

      if (error) {
        console.error('Database connection test failed:', error);
        return false;
      }

      console.log('Database connection test successful:', data);
      return true;
    } catch (err) {
      console.error('Database connection test error:', err);
      return false;
    }
  };

  if (isLoading) {
    return (
      <div className="blog-admin-page">
        <Header />
        <main className="admin-content">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p className="loading-message">Loading blog posts...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="blog-admin-page">
      <Header />
      <main className="admin-content">
        <div className="admin-header">
          <h1 className="admin-title">Blog Admin</h1>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <form className="blog-form">
          <div className="form-group">
            <label htmlFor="title">Title:</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="slug">Slug:</label>
            <input
              type="text"
              id="slug"
              name="slug"
              value={formData.slug}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="content">Content:</label>
            <div className="quill-container">
              <ReactQuill
                ref={quillRef}
                theme="snow"
                modules={modules}
                formats={formats}
                value={formData.content}
                onChange={handleContentChange}
                preserveWhitespace={true}
                placeholder="Write your blog post content here..."
                bounds=".quill-container"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="excerpt">Excerpt:</label>
            <textarea
              id="excerpt"
              name="excerpt"
              value={formData.excerpt}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="featured_image">Featured Image URL:</label>
            <input
              type="text"
              id="featured_image"
              name="featured_image"
              value={formData.featured_image}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="published"
                checked={formData.published}
                onChange={handleInputChange}
              />
              Published
            </label>
          </div>

          <div className="button-group">
            {editingPost ? (
              <>
                <button 
                  type="button"
                  disabled={isSubmitting}
                  onClick={handleSubmit}
                >
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
                <button type="button" onClick={handleCancel}>
                Cancel
                </button>
              </>
            ) : (
              <button 
                type="button"
                disabled={isSubmitting}
                onClick={handleSubmit}
              >
                {isSubmitting ? 'Saving...' : 'Create Post'}
              </button>
            )}
          </div>
        </form>

        <div className="posts-list">
          <h2>All Posts</h2>
          {posts.map((post) => (
            <div key={post.id} className="post-item">
              <div className="post-info">
                <h3>{post.title}</h3>
                <p className="post-meta">
                  <span className={`status ${post.published ? 'published' : 'draft'}`}>
                    {post.published ? 'Published' : 'Draft'}
                  </span>
                  <span className="date">
                    {new Date(post.created_at).toLocaleDateString()}
                  </span>
                </p>
              </div>
              <div className="post-actions">
                <button 
                  onClick={() => togglePublishedStatus(post.id, post.published)} 
                  className={`publish-button ${post.published ? 'unpublish' : 'publish'}`}
                  style={{ 
                    marginRight: '0.5rem', 
                    padding: '0.25rem 0.5rem', 
                    backgroundColor: post.published ? '#ef4444' : '#10b981', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '0.25rem', 
                    cursor: 'pointer' 
                  }}
                >
                  {post.published ? 'Unpublish' : 'Publish'}
                </button>
                <button onClick={() => handleEdit(post)} className="edit-button">
                  Edit
                </button>
                <button onClick={() => handleDelete(post.id)} className="delete-button">
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BlogAdminPage; 